import { v4 as uuid } from 'uuid';
import { AccountInterface, RpcProvider } from 'starknet';
import { getAccountAddressFromId, getTokenAmountTransferredInReceipt, waitForTransactionReceipt } from '../../utils';
import { Decimal } from '../../datastructures';
import { Asset, InvokeResponse } from '../../interfaces';
import { Nullable } from '../../interfaces';
import { DecimalFormat } from '@tempusfinance/decimal';
import { addToast } from '../toast';
import { LentDebtTokenContract } from '../../services/contracts/LentDebtTokenContract';
import { createAsyncAtom, emitAtomEvent } from '../../lib/state';
import { pauseBalanceRefresh, resumeBalanceRefresh } from './pauseBalanceRefresh';
import { borrowInput } from './lendAndBorrowInputAtom';

interface BorrowRequest {
  asset: Asset;
  amount: Decimal;
  onBehalfOf: string;
  accountId: number;
  txnId: string;
  provider: RpcProvider;
}

interface BorrowStatus {
  pending: boolean;
  request: BorrowRequest;
  success?: boolean;
  error?: Error;
  getTransactionResponse?: InvokeResponse;
  transactionData?: {
    borrowedAmount: Decimal;
  };
  txnId: string;
}

/* interface BorrowResponse {
  request: BorrowRequest;
  getTransactionResponse?: InvokeTransactionResponse | void;
  getTransactionReceipt?: InvokeTransactionReceiptResponse;
  transactionData?: {
    borrowedAmount: Decimal;
    gasFee: Decimal;
  };
  error?: Error;
  txnId: string;
} */

const lendAndBorrowBorrowStatusAtom = createAsyncAtom<Nullable<BorrowStatus>>(async () => null);

const lendAndBorrowBorrow = async (walletAccount: AccountInterface, request: BorrowRequest) => {
  const { asset, amount, accountId, txnId, provider } = request;

  const debtTokenContract = new LentDebtTokenContract(asset.tokenPrecision, asset.debtTokenAddress, walletAccount);

  if (!debtTokenContract) {
    return null;
  }

  const accountAddress = getAccountAddressFromId(walletAccount.address, accountId);

  const amountFormatted = DecimalFormat.format(amount, {
    style: 'currency',
    currency: asset.token,
    fractionDigits: asset.uiTokenPrecision,
    pad: true,
  });

  pauseBalanceRefresh();
  try {
    lendAndBorrowBorrowStatusAtom.setValue({
      pending: true,
      request,
      txnId,
    });

    let transactionResponse: Nullable<InvokeResponse> = null;
    try {
      transactionResponse = await debtTokenContract.mint(amount, accountAddress, walletAccount);
    } catch (error) {
      lendAndBorrowBorrowStatusAtom.setValue({
        pending: false,
        success: false,
        error: new Error('Failed to borrow!'),
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Borrow ${amountFormatted}`,
      });

      return;
    }

    addToast({
      id: uuid(),
      type: 'info',
      message: `Borrow ${amountFormatted}`,
      txId: transactionResponse?.transactionHash,
    });

    if (!transactionResponse) {
      const userRejectError = new Error('Rejected by user.');
      lendAndBorrowBorrowStatusAtom.setValue({
        pending: false,
        success: false,
        error: userRejectError,
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Borrow ${amountFormatted}`,
      });

      return;
    }

    const transactionReceipt = await waitForTransactionReceipt(
      transactionResponse.transactionHash,
      walletAccount,
      provider,
    );

    if (!transactionReceipt) {
      const receiptFetchFailed = new Error('Failed to fetch borrow transaction receipt!');
      lendAndBorrowBorrowStatusAtom.setValue({
        pending: false,
        success: false,
        error: receiptFetchFailed,
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Borrow ${amountFormatted}`,
        txId: transactionResponse.transactionHash,
      });

      return;
    }

    if (transactionReceipt.isReverted()) {
      const txnReverted = new Error('Transaction reverted!');
      lendAndBorrowBorrowStatusAtom.setValue({
        pending: false,
        success: false,
        error: txnReverted,
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Borrow ${amountFormatted}`,
        txId: transactionResponse.transactionHash,
      });
    }

    if (transactionReceipt.isSuccess()) {
      const borrowedAmount = getTokenAmountTransferredInReceipt(transactionReceipt, request.asset);

      lendAndBorrowBorrowStatusAtom.setValue({
        pending: false,
        success: true,
        request,
        getTransactionResponse: transactionResponse,
        transactionData: {
          borrowedAmount,
        },
        txnId,
      });
    }

    emitAtomEvent('lend-and-borrow-borrow');

    borrowInput.reset();

    addToast({
      id: uuid(),
      type: 'success',
      message: `Borrowed **${amountFormatted}**`,
      txId: transactionResponse.transactionHash,
    });
  } finally {
    resumeBalanceRefresh();
  }
};

export { lendAndBorrowBorrowStatusAtom, lendAndBorrowBorrow };
